import _ from 'lodash';
import pluralize from 'pluralize';
import React, {useState, FunctionComponent, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Modal} from '@material-ui/core';
import {ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {CloseIcon, StoreIcon} from '@treez-ui/core/icons';
import Checkbox from '@treez-ui/core/forms/Checkbox';
import {CurrentSelectionStateContext} from '~/lib/selectedProductContext';
import {GlobalApplicationState} from '~/redux/reducers/RootReducer';
import {OrganizationState} from '~/redux/reducers/OrganizationReducer';
import {selectOrganizationState} from '~/redux/selectors/organizationSelectors';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {addProductsToCatalogs, removeProductsFromCatalogs} from '~/utils/ProductAPI';
import {onBackdropClickWrap} from '~/lib/backdropClickWrap';

export enum StoreSelectorAction {
    ADD = 'add',
    REMOVE = 'remove',
}

const {ADD, REMOVE} = StoreSelectorAction;

interface Props {
    addOrRemove: StoreSelectorAction;
    close: Function;
    open: boolean;
}

const StoreSelectorForMultipleProducts: FunctionComponent<Props> = (props: Props) => {
    const {addOrRemove, close, open} = props;

    const [catalogsSelected, setCatalogsSelected] = useState<number[]>([]);

    const selectedProducts = useContext(CurrentSelectionStateContext);
    const organizationState = useSelector<GlobalApplicationState, OrganizationState>(
        selectOrganizationState
    );

    const {stores} = organizationState;
    const dispatch = useDispatch();

    const selectionCount = _.size(selectedProducts);

    const handleCatalogSelection = (catalogId, include) => {
        if (catalogIsSelected(catalogId) === false && include === true) {
            setCatalogsSelected([...catalogsSelected, catalogId]);
        } else if (catalogIsSelected(catalogId) && include === false) {
            setCatalogsSelected(_.without(catalogsSelected, catalogId));
        }
    };

    const catalogIsSelected = (catalogId: number) => {
        return _.includes(catalogsSelected, catalogId);
    };

    const addProductsHeader = `Add ${pluralize('Product', selectionCount)} to Stores`;
    const removeProductsHeader = `Remove ${pluralize('Product', selectionCount)} from Stores`;
    const ModalHeader = addOrRemove == ADD ? addProductsHeader : removeProductsHeader;

    const clearAndCloseModal = () => {
        setCatalogsSelected([]);
        close();
    };

    const addProducts = async (selectedCatalogs: number[], products: ProductInCatalog[]) => {
        try {
            const productIds = _.map(products, 'id');

            await addProductsToCatalogs(selectedCatalogs, productIds);

            dispatch(
                showSnackbar({
                    content: `Product added to selected ${pluralize(
                        'catalog',
                        selectedCatalogs.length
                    )}`,
                    onClose: () => dispatch(clearSnackbar()),
                    type: SnackbarType.SUCCESS,
                })
            );

            close();
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, 'An Error Has Occured', {
                bodyText: `${pluralize('Product', products.length)} could not be added:
                        ${error.message}`,
                type: ModalType.ERROR,
            });
        }
        close();
    };

    const removeProducts = async (catalogs: number[], products: ProductInCatalog[]) => {
        try {
            const productIds = _.map(products, 'id');

            await removeProductsFromCatalogs(catalogs, productIds);
            close();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Modal open={open} onClose={onBackdropClickWrap(() => clearAndCloseModal())}>
            <div className="multi-product-stores-modal">
                <div className="add-to-stores-header">
                    <div className="add-to-stores-headline">
                        <StoreIcon />
                        <div className="add-to-stores-prompt">{ModalHeader}</div>
                    </div>
                    <button onClick={() => clearAndCloseModal()}>
                        <CloseIcon />
                    </button>
                </div>
                <div className="store-list">
                    {_.map(stores, (store) => {
                        const {name, catalogId} = store;
                        if (catalogId == null) {
                            dispatch(
                                showSnackbar({
                                    onClose: () => dispatch(clearSnackbar()),
                                    type: SnackbarType.WARNING,
                                    content: `Store catalog is missing for ${store.name}, please contact support@treez.io`,
                                })
                            );
                            console.error(`Catalog for ${name} could not be loaded`);
                            return;
                        }
                        return (
                            <button className="store-select-button" key={`store-${store.id}`}>
                                <Checkbox
                                    checked={catalogIsSelected(catalogId)}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const isChecked = e.target.checked;
                                        handleCatalogSelection(catalogId, isChecked);
                                    }}
                                />
                                <div className="store-select-store-name">{name}</div>
                            </button>
                        );
                    })}
                </div>
                {addOrRemove === ADD && (
                    <button
                        className="add-to-stores-modal-button"
                        onClick={() => {
                            addProducts(catalogsSelected, selectedProducts);
                        }}
                    >
                        Add To Stores
                    </button>
                )}
                {addOrRemove === REMOVE && (
                    <button
                        className="remove-from-stores-modal-button"
                        onClick={() => {
                            removeProducts(catalogsSelected, selectedProducts);
                        }}
                    >
                        Remove From Stores
                    </button>
                )}
            </div>
        </Modal>
    );
};

export default StoreSelectorForMultipleProducts;
